import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageQueryOnLinks } from '../components/site-link';
import type { LegalSlug } from '@shared/content-schemas';
import { useUrlLocale } from '../i18n/url-locale';
import { getLegalPage } from '../lib/docs';
import { formatDate } from '../lib/format-date';
import { usePageTitle } from '../lib/use-page-title';
import { NotFoundPage } from './not-found';

/** A legal page. Drafts carry a prominent "Draft — not yet in effect" banner. */
export function LegalPageView({ slug }: { slug: LegalSlug }) {
  const { t } = useTranslation();
  const locale = useUrlLocale();
  const page = getLegalPage(slug, locale);
  const proseRef = useRef<HTMLDivElement>(null);
  useLanguageQueryOnLinks(proseRef, slug);
  usePageTitle(`/${slug}`, page?.title ?? t('notFound.title'));
  if (!page) return <NotFoundPage />;
  const draft = page.status === 'draft';
  return (
    <div className="container page narrow">
      <article className="prose">
        {draft && (
          <div className="draft-banner" role="note">
            <p>
              <strong>{t('legal.draftTitle')}</strong>
            </p>
            <p>{t('legal.draftBody')}</p>
          </div>
        )}
        <h1>{page.title}</h1>
        <p className="meta">
          {page.effectiveDate === 'TODO(owner-approval)'
            ? t('legal.effectiveDatePending')
            : t('legal.effectiveDate', { date: formatDate(page.effectiveDate, locale) })}
          {' · '}
          <time dateTime={page.updated}>{t('legal.updated', { date: formatDate(page.updated, locale) })}</time>
        </p>
        <div ref={proseRef} dangerouslySetInnerHTML={{ __html: page.html }} />
      </article>
    </div>
  );
}
